<template>
    <div class="workflow-timeline" :class="{ compact: compact }">
        <div v-if="!hideTitle" class="timeline-header">
            <h2 class="timeline-title">{{ title || "프로세스 현황" }}</h2>
        </div>

        <div v-if="hasSteps" class="timeline-wrapper">
            <div class="timeline-container">
                <div class="timeline-track">
                    <div class="timeline-progress-bar" :style="progressLineStyle"></div>
                </div>

                <ol class="timeline-list">
                    <li
                        v-for="(step, index) in steps"
                        :key="step.stepIdx || step.period || 'step-' + index"
                        class="timeline-item"
                        :class="[
                            getNodeStatusClass(step),
                            getCheckpointStyleClass(step),
                            { 'is-selected': selectedStepIdx != null && step.stepIdx === selectedStepIdx },
                        ]"
                        :ref="
                            (el) => {
                                if (el) itemRefs[index] = el;
                            }
                        "
                    >
                        <div class="timeline-date">
                            <div class="timeline-date-row timeline-date-row-badges">
                                <span
                                    v-if="step.isCheckpoint"
                                    class="checkpoint-badge"
                                    :class="getCheckpointBadgeClass(step)"
                                >
                                    <span class="checkpoint-badge-quarter"> 체크포인트</span>
                                </span>
                            </div>
                            <div class="timeline-date-row timeline-date-row-periods">
                                <span v-if="step.period" class="period">{{ formatPeriodDisplay(step.period) }}</span>
                            </div>
                        </div>

                        <div class="marker-wrapper marker-wrapper-with-dropdown">
                            <CustomDropdown
                                class="step-action-dropdown"
                                menu-class="step-action-menu"
                                align="left"
                                :items="getStepDropdownItems(step)"
                            >
                                <template #trigger>
                                    <div class="timeline-marker">
                                        <i :class="getMarkerIcon(step)"></i>
                                    </div>
                                </template>
                            </CustomDropdown>
                        </div>

                        <div v-if="!compact" class="status-summary">
                            <!-- 1. 배출량 (위) -->
                            <div v-if="hasEmissionsData(step)" class="step-emissions">
                                <div v-if="step.totalEmissions != null" class="step-emissions-total">
                                    <span class="step-emissions-value">{{
                                        step.totalEmissions ? formatCarbonAutoWithUnit(step.totalEmissions) : "-"
                                    }}</span>
                                </div>
                                <div class="step-emissions-scopes">
                                    <div class="step-emissions-scope-row">
                                        <span class="step-emissions-scope-label">S1</span>
                                        <span class="step-emissions-scope-value">{{
                                            formatCarbonAutoWithUnit(
                                                step.scope1Emissions,
                                                "kgCO2e",
                                                getScopeEmissionsFormat(),
                                            )
                                        }}</span>
                                    </div>
                                    <div class="step-emissions-scope-row">
                                        <span class="step-emissions-scope-label">S2</span>
                                        <span class="step-emissions-scope-value">{{
                                            formatCarbonAutoWithUnit(
                                                step.scope2Emissions,
                                                "kgCO2e",
                                                getScopeEmissionsFormat(),
                                            )
                                        }}</span>
                                    </div>
                                    <div class="step-emissions-scope-row">
                                        <span class="step-emissions-scope-label">S3</span>
                                        <span class="step-emissions-scope-value">{{
                                            formatCarbonAutoWithUnit(
                                                step.scope3Emissions,
                                                "kgCO2e",
                                                getScopeEmissionsFormat(),
                                            )
                                        }}</span>
                                    </div>
                                </div>
                            </div>
                            <!-- 2. 수집/산정 상태 칩 — 클릭으로 상태 전환 (팝오버 또는 직접 실행) -->
                            <div class="status-summary-top">
                                <!-- 수집 칩 -->
                                <div class="chip-wrap">
                                    <div
                                        class="status-chip"
                                        :class="[
                                            `chip-coll-${(step.collStat || 'pending').toLowerCase()}`,
                                            { 'is-clickable': getAllCollTransitions(step).length > 0 },
                                            { 'is-open': activePopup === `${step.stepIdx}-coll` },
                                        ]"
                                        :title="getCollChipTooltip(step)"
                                        @click.stop="handleCollChipClick(step)"
                                    >
                                        <span class="chip-dot"></span>
                                        <span class="chip-label">수집</span>
                                        <span class="chip-value">{{ getCollStatusLabel(step.collStat) }}</span>
                                        <i
                                            v-if="getAllCollTransitions(step).length > 0"
                                            :class="[
                                                needsPopup(getAllCollTransitions(step))
                                                    ? 'fas fa-chevron-down'
                                                    : 'fas fa-chevron-right',
                                                'chip-arrow',
                                                { 'chip-arrow-open': activePopup === `${step.stepIdx}-coll` },
                                            ]"
                                        ></i>
                                    </div>
                                    <transition name="popup-fade">
                                        <div
                                            v-if="activePopup === `${step.stepIdx}-coll`"
                                            class="status-popup"
                                            @click.stop
                                        >
                                            <button
                                                v-for="t in getAllCollTransitions(step)"
                                                :key="t.value"
                                                class="popup-item"
                                                :class="{ 'popup-item-backward': t.backward }"
                                                @click.stop="executeTransition(step, 'coll', t.value)"
                                            >
                                                <i
                                                    :class="t.backward ? 'fas fa-undo-alt' : 'fas fa-arrow-right'"
                                                    class="popup-item-icon"
                                                ></i>
                                                <span class="popup-item-label">{{ t.label }}</span>
                                            </button>
                                        </div>
                                    </transition>
                                </div>
                                <!-- 산정 칩 -->
                                <div class="chip-wrap">
                                    <div
                                        class="status-chip"
                                        :class="[
                                            `chip-calc-${(step.calcStat || 'pending').toLowerCase()}`,
                                            { 'is-clickable': getAllCalcTransitions(step).length > 0 },
                                            { 'is-open': activePopup === `${step.stepIdx}-calc` },
                                        ]"
                                        :title="getCalcChipTooltip(step)"
                                        @click.stop="handleCalcChipClick(step)"
                                    >
                                        <span class="chip-dot"></span>
                                        <span class="chip-label">산정</span>
                                        <span class="chip-value">{{ getCalcStatusLabel(step.calcStat) }}</span>
                                        <i
                                            v-if="getAllCalcTransitions(step).length > 0"
                                            :class="[
                                                needsPopup(getAllCalcTransitions(step))
                                                    ? 'fas fa-chevron-down'
                                                    : 'fas fa-chevron-right',
                                                'chip-arrow',
                                                { 'chip-arrow-open': activePopup === `${step.stepIdx}-calc` },
                                            ]"
                                        ></i>
                                    </div>
                                    <transition name="popup-fade">
                                        <div
                                            v-if="activePopup === `${step.stepIdx}-calc`"
                                            class="status-popup"
                                            @click.stop
                                        >
                                            <button
                                                v-for="t in getAllCalcTransitions(step)"
                                                :key="t.value"
                                                class="popup-item"
                                                :class="{ 'popup-item-backward': t.backward }"
                                                @click.stop="executeTransition(step, 'calc', t.value)"
                                            >
                                                <i
                                                    :class="t.backward ? 'fas fa-undo-alt' : 'fas fa-arrow-right'"
                                                    class="popup-item-icon"
                                                ></i>
                                                <span class="popup-item-label">{{ t.label }}</span>
                                            </button>
                                        </div>
                                    </transition>
                                </div>
                            </div>
                            <div v-if="step.isCheckpoint" class="checkpoint-confirm-wrap">
                                <button
                                    type="button"
                                    class="btn-checkpoint-confirm"
                                    :title="getCheckpointButtonTitle(step)"
                                    :disabled="isCheckpointButtonDisabled(step)"
                                    @click.stop="handleCheckpointConfirmClick(step)"
                                >
                                    <span>{{ getCheckpointButtonLabel(step) }}</span>
                                </button>
                            </div>
                            <div v-else class="status-summary-spacer"></div>
                        </div>
                    </li>
                </ol>
            </div>
        </div>

        <div v-else class="timeline-placeholder">
            <i class="fas fa-calendar-check"></i>
            <p class="placeholder-title">월별 산정 데이터가 없습니다</p>
            <p class="placeholder-desc">해당 연도에 등록된 월별 단계가 없습니다</p>
        </div>

        <WorkflowFormModal
            ref="workflowModal"
            :is-edit-mode="isEditMode"
            :initial-data="formData"
            :org-options="orgOptions || []"
            :saving="saving"
            @save="handleSave"
            @hidden="handleModalHidden"
        />

        <b-modal ref="deleteModal" title="워크플로우 삭제" size="sm" centered @ok="handleDelete">
            <p>정말로 이 워크플로우를 삭제하시겠습니까?</p>
            <p class="text-danger small" v-if="selectedStep">
                <strong>{{ selectedStep.year }}년</strong> 워크플로우가 영구 삭제됩니다.
            </p>
        </b-modal>

        <!-- 증빙자료 누락 → 강제생성/강제검증 확인 모달 (워크플로우 생성 / 체크포인트 검증·재검증) -->
        <EvidenceForceConfirmModal
            ref="evidenceForceConfirmModal"
            :error="evidenceModalError"
            :title="evidenceModalPayload?.action ? '강제검증 확인' : '강제생성 확인'"
            :confirm-button-text="evidenceModalPayload?.action ? '강제검증' : '강제생성'"
            @confirm="handleForceConfirm"
            @hidden="onEvidenceModalHidden"
        />
    </div>
</template>

<script>
import CarbonCodes from "../../../codes";
import carbonApi from "../../../api";
import { unwrapResponse, extractErrorMessage } from "../../../utils/api.js";
import { formatCarbonAutoWithUnit } from "../../../utils/convertUnit.js";
import WorkflowFormModal from "../modals/WorkflowFormModal.vue";
import EvidenceForceConfirmModal from "../../common/EvidenceForceConfirmModal.vue";
import CustomDropdown from "../../common/CustomDropdown.vue";

export default {
    name: "WorkflowTimeline",
    components: { WorkflowFormModal, EvidenceForceConfirmModal, CustomDropdown },
    props: {
        steps: { type: Array, required: true },
        /** 워크플로우 periodStat - CONFIRMED/CANCELLED이면 드롭다운 비활성화 */
        periodStat: { type: String, default: null },
        orgOptions: { type: Array, default: () => [] },
        title: { type: String, default: "프로세스 현황" },
        hideTitle: { type: Boolean, default: false },
        /** true면 날짜+마커만 표시, 상세(수집/산정/확정/kgCO2e)는 표시하지 않음 → 다른 영역(패널 테이블)에서 표시용 */
        compact: { type: Boolean, default: false },
        /** 부모에서 선택된 스텝 인덱스 (해당 노드 강조용) */
        selectedStepIdx: { type: Number, default: null },
    },
    data() {
        return {
            itemRefs: [],
            isEditMode: false,
            saving: false,
            selectedStep: null,
            formData: {},
            /** 409 canForceConfirm 시 표시용 (error 객체 또는 { step, action: 'verify'|'reverify' } / 워크플로우 생성 payload) */
            evidenceModalError: null,
            evidenceModalPayload: null,
            /** 현재 열린 팝오버 키 (e.g. "18-coll", "18-calc") */
            activePopup: null,
        };
    },
    mounted() {
        document.addEventListener("click", this.closePopup);
        document.addEventListener("keydown", this._onKeydown);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closePopup);
        document.removeEventListener("keydown", this._onKeydown);
    },
    computed: {
        hasSteps() {
            return this.steps && this.steps.length > 0;
        },
        /** periodStat이 CONFIRMED/CANCELLED이면 드롭다운 버튼 비활성화 */
        isDropdownDisabled() {
            const s = (this.periodStat || "").toUpperCase();
            return s === "CONFIRMED" || s === "CANCELLED";
        },
        progressLineStyle() {
            if (!this.steps || this.steps.length < 2) return { width: "0%" };
            let lastCompletedIndex = -1;
            for (let i = 0; i < this.steps.length; i++) {
                if (this.isStepCompleted(this.steps[i])) {
                    lastCompletedIndex = i;
                } else {
                    break;
                }
            }
            if (lastCompletedIndex === -1) return { width: "0%" };
            const percentage = (lastCompletedIndex / (this.steps.length - 1)) * 100;
            return { width: `${percentage}%` };
        },
    },
    methods: {
        isStepCalculated(step) {
            return step.calcStat === "CALCULATED";
        },
        isStepCompleted(step) {
            return step.calcStat === "VERIFIED";
        },
        isStepInProgress(step) {
            return step.collStat === "COLLECTING";
        },
        isCollectionDoneAwaitingCalc(step) {
            return step.collStat === "COMPLETED" && step.calcStat === "PENDING";
        },
        getNodeStatusClass(step) {
            if (this.isStepCompleted(step)) return "status-completed";
            if (this.isStepCalculated(step)) return "status-calculated";
            if (this.isCollectionDoneAwaitingCalc(step)) return "status-collection-done";
            if (this.isStepInProgress(step)) return "status-in-progress";
            return "status-pending";
        },
        formatPeriodDisplay(period) {
            if (!period || period.length < 6) return "-";
            return `${period.substring(0, 4)}.${period.substring(4, 6)}`;
        },
        /** 체크포인트 뱃지 스타일: 검증완료=초록, 대기=그레이 */
        getCheckpointBadgeClass(step) {
            if (!step.isCheckpoint) return "";
            return this.isStepCompleted(step) ? "checkpoint-badge-verified" : "checkpoint-badge-pending";
        },
        /** 카드 배경/테두리용: 검증완료 체크포인트, 체크포인트 대기, 비체크포인트 대기 */
        getCheckpointStyleClass(step) {
            if (step.isCheckpoint && this.isStepCompleted(step)) return "checkpoint-verified";
            if (step.isCheckpoint && this.getNodeStatusClass(step) === "status-pending") return "checkpoint-pending";
            return "";
        },
        getCollStatusLabel(status) {
            if (!status) return "-";
            return CarbonCodes.collStat.getLabel(status);
        },
        getCalcStatusLabel(status) {
            if (!status) return "-";
            return CarbonCodes.calcStat.getLabel(status);
        },
        formatCarbonAutoWithUnit,
        /** Scope 배출량: 소수점 없이 정수만 (옵션 객체로 명시 전달) */
        getScopeEmissionsFormat() {
            return { maxFractionDigits: 3, minFractionDigits: 0 };
        },
        /** 총량 또는 scope 중 하나라도 있으면 배출량 영역 표시 */
        hasEmissionsData(step) {
            return (
                step.totalEmissions != null ||
                step.scope1Emissions != null ||
                step.scope2Emissions != null ||
                step.scope3Emissions != null
            );
        },

        /**
         * 수집 상태 — 전이 가능한 모든 액션 반환
         * backward: true = 역방향 전이 (주의 표시용)
         */
        getAllCollTransitions(step) {
            if (this.isDropdownDisabled) return [];
            const coll = (step.collStat || "PENDING").toUpperCase();
            if (coll === "PENDING") {
                return [{ value: "COLLECTING", label: "수집 시작", backward: false }];
            }
            if (coll === "COLLECTING") {
                return [
                    { value: "COMPLETED", label: "수집 완료", backward: false },
                    { value: "PENDING", label: "수집 취소", backward: true },
                ];
            }
            if (coll === "COMPLETED") {
                return [
                    { value: "COLLECTING", label: "수집 재개", backward: true },
                    { value: "PENDING", label: "수집 초기화", backward: true },
                ];
            }
            return [];
        },
        /**
         * 산정 상태 — 전이 가능한 모든 액션 반환
         * VERIFIED는 verify-checkpoint API 전용이므로 제외
         */
        getAllCalcTransitions(step) {
            if (this.isDropdownDisabled) return [];
            const coll = (step.collStat || "").toUpperCase();
            const calc = (step.calcStat || "PENDING").toUpperCase();
            if (coll !== "COMPLETED") return [];
            if (calc === "PENDING") {
                return [{ value: "CALCULATED", label: "산정 완료", backward: false }];
            }
            if (calc === "CALCULATED") {
                return [{ value: "PENDING", label: "산정 초기화", backward: true }];
            }
            return [];
        },
        /**
         * 팝오버가 필요한 경우 판별
         * - 선택지가 2개 이상이거나
         * - 역방향 전이만 있는 경우 (실수 방지를 위해 팝오버 확인 필요)
         */
        needsPopup(transitions) {
            if (transitions.length === 0) return false;
            if (transitions.length > 1) return true;
            return transitions[0].backward;
        },
        getCollChipTooltip(step) {
            if (this.isDropdownDisabled) return "";
            const transitions = this.getAllCollTransitions(step);
            if (!transitions.length) return "";
            if (this.needsPopup(transitions)) return "클릭하여 수집 상태 변경";
            return `클릭하여 "${transitions[0].label}"(으)로 변경`;
        },
        getCalcChipTooltip(step) {
            if (this.isDropdownDisabled) return "";
            const coll = (step.collStat || "").toUpperCase();
            if (coll !== "COMPLETED") return "수집 완료 후 산정 가능";
            const transitions = this.getAllCalcTransitions(step);
            if (!transitions.length) return "";
            if (this.needsPopup(transitions)) return "클릭하여 산정 상태 변경";
            return `클릭하여 "${transitions[0].label}"(으)로 변경`;
        },
        handleCollChipClick(step) {
            if (this.isDropdownDisabled) return;
            const transitions = this.getAllCollTransitions(step);
            if (!transitions.length) return;
            if (this.needsPopup(transitions)) {
                const key = `${step.stepIdx}-coll`;
                this.activePopup = this.activePopup === key ? null : key;
            } else {
                this.updateStepStatus(step, { collStat: transitions[0].value });
            }
        },
        handleCalcChipClick(step) {
            if (this.isDropdownDisabled) return;
            const transitions = this.getAllCalcTransitions(step);
            if (!transitions.length) return;
            if (this.needsPopup(transitions)) {
                const key = `${step.stepIdx}-calc`;
                this.activePopup = this.activePopup === key ? null : key;
            } else {
                this.updateStepStatus(step, { calcStat: transitions[0].value });
            }
        },
        /** 팝오버에서 선택한 전이 실행 */
        executeTransition(step, type, value) {
            this.activePopup = null;
            if (type === "coll") {
                this.updateStepStatus(step, { collStat: value });
            } else if (type === "calc") {
                this.updateStepStatus(step, { calcStat: value });
            }
        },
        /** 팝오버 닫기 (외부 클릭 or Esc) */
        closePopup() {
            this.activePopup = null;
        },
        _onKeydown(e) {
            if (e.key === "Escape") this.closePopup();
        },
        /** PATCH workflow-steps/:stepIdx/status - body: { collStat?, calcStat? } */
        async updateStepStatus(step, opt) {
            if (!step.stepIdx) return;
            const payload = {};
            if (opt.collStat) payload.collStat = opt.collStat;
            if (opt.calcStat) payload.calcStat = opt.calcStat;
            if (Object.keys(payload).length === 0) return;
            try {
                const res = await carbonApi.workflow.updateWorkflowStepStatus(step.stepIdx, payload);
                unwrapResponse(res, { defaultMessage: "상태 변경에 실패했습니다." });
                this.$emit("refresh");
                this.$bvToast?.toast(res.data.message, { variant: "success", title: "완료" });
            } catch (e) {
                const msg = extractErrorMessage(e, "상태 변경에 실패했습니다.");
                this.$bvToast?.toast(msg, { variant: "danger", title: "실패" });
            }
        },
        /** 체크포인트 검증/재검증 버튼 클릭 */
        handleCheckpointConfirmClick(step) {
            if (!step.stepIdx || this.isCheckpointButtonDisabled(step)) return;
            const isReverify = (step.calcStat || "").toUpperCase() === "VERIFIED";
            const msg = isReverify
                ? "이 체크포인트를 재검증하시겠습니까?"
                : "이 체크포인트를 검증하시겠습니까?\n분기/연간 데이터가 월별로 분배된 후 재계산됩니다.";
            if (!confirm(msg)) return;
            if (isReverify) {
                this.reverifyCheckpoint(step);
            } else {
                this.verifyCheckpoint(step);
            }
        },
        getCheckpointButtonLabel(step) {
            return (step.calcStat || "").toUpperCase() === "VERIFIED" ? "재검증" : "검증";
        },
        getCheckpointButtonTitle(step) {
            return this.isCheckpointButtonDisabled(step) ? "조건 미충족" : "체크포인트 검증";
        },
        isCheckpointButtonDisabled(step) {
            if (this.isDropdownDisabled) return true;
            const coll = (step.collStat || "").toUpperCase();
            const calc = (step.calcStat || "").toUpperCase();
            if (coll !== "COMPLETED") return true;
            return calc !== "CALCULATED" && calc !== "VERIFIED";
        },
        /** POST workflow-steps/:stepIdx/verify-checkpoint (체크포인트 버튼용) */
        async verifyCheckpoint(step) {
            if (!step.stepIdx) return;
            try {
                const response = await carbonApi.workflow.verifyWorkflowStepCheckpoint(step.stepIdx);
                unwrapResponse(response);
                this.$emit("refresh");
                this.$bvToast?.toast(response.data.message, { variant: "success", title: "완료" });
            } catch (error) {
                const data = error?.response?.data ?? error?.payload ?? null;
                const err = data?.error ?? data;
                if (err?.canForceConfirm === true) {
                    this.evidenceModalError = err;
                    this.evidenceModalPayload = { step, action: "verify" };
                    this.$nextTick(() => this.$refs.evidenceForceConfirmModal?.show());
                } else {
                    this.$bvToast?.toast(extractErrorMessage(error, "체크포인트 검증에 실패했습니다."), {
                        variant: "danger",
                    });
                }
            }
        },
        /** POST workflow-steps/:stepIdx/reverify-checkpoint */
        async reverifyCheckpoint(step) {
            if (!step.stepIdx) return;
            try {
                const response = await carbonApi.workflow.reverifyWorkflowStepCheckpoint(step.stepIdx);
                unwrapResponse(response);
                this.$emit("refresh");
                this.$bvToast?.toast(response.data.message, { variant: "success", title: "완료" });
            } catch (error) {
                const data = error?.response?.data ?? error?.payload ?? null;
                const err = data?.error ?? data;
                if (err?.canForceConfirm === true) {
                    this.evidenceModalError = err;
                    this.evidenceModalPayload = { step, action: "reverify" };
                    this.$nextTick(() => this.$refs.evidenceForceConfirmModal?.show());
                } else {
                    this.$bvToast?.toast(extractErrorMessage(error, "체크포인트 재검증에 실패했습니다."), {
                        variant: "danger",
                    });
                }
            }
        },
        getMarkerIcon(step) {
            if (this.isStepCompleted(step)) return "fas fa-check";
            if (this.isStepCalculated(step)) return "fas fa-calculator";
            if (this.isCollectionDoneAwaitingCalc(step)) return "fas fa-clipboard-check";
            if (this.isStepInProgress(step)) return "fas fa-sync-alt fa-spin";
            return "fas fa-lock";
        },
        getStepDropdownItems(step) {
            return [
                {
                    icon: "fa-inbox",
                    label: "인벤토리",
                    onClick: () => this.handleStepSelect(step, "inventory"),
                },
                {
                    icon: "fa-upload",
                    label: "활동자료",
                    onClick: () => this.handleStepSelect(step, "activity"),
                },
            ];
        },
        handleStepSelect(step, viewMode) {
            this.$emit("step-click", { step, viewMode });
        },

        handleModalHidden() {
            this.isEditMode = false;
            this.formData = {};
        },
        async handleSave(payload) {
            try {
                this.saving = true;
                let response = this.isEditMode
                    ? await carbonApi.workflow.updateInventoryWorkflow(this.selectedStep.workflowIdx, payload)
                    : await carbonApi.workflow.createInventoryWorkflow(payload);
                unwrapResponse(response);
                this.$refs.workflowModal.hide();
                this.$emit("refresh");
            } catch (error) {
                const data = error?.response?.data ?? error?.payload ?? null;
                const err = data?.error ?? data;
                if (
                    !this.isEditMode &&
                    (error?.response?.status === 409 || data?.success === false) &&
                    err?.code === "EVIDENCE_MISSING" &&
                    err?.canForceConfirm === true
                ) {
                    this.evidenceModalError = err;
                    this.evidenceModalPayload = payload;
                    this.$nextTick(() => this.$refs.evidenceForceConfirmModal?.show());
                } else {
                    this.$bvToast?.toast(extractErrorMessage(error, "저장에 실패했습니다."), {
                        variant: "danger",
                        title: "저장 실패",
                    });
                    console.error(error);
                }
            } finally {
                this.saving = false;
            }
        },
        async handleForceConfirm(forceConfirmValue) {
            if (!this.evidenceModalPayload) return;
            const payload = this.evidenceModalPayload;
            const isCheckpoint = payload.step != null && payload.action != null;

            try {
                if (isCheckpoint) {
                    this.saving = true;
                    const body = { forceConfirm: forceConfirmValue };
                    const response =
                        payload.action === "reverify"
                            ? await carbonApi.workflow.reverifyWorkflowStepCheckpoint(payload.step.stepIdx, body)
                            : await carbonApi.workflow.verifyWorkflowStepCheckpoint(payload.step.stepIdx, body);
                    unwrapResponse(response);
                    this.evidenceModalError = null;
                    this.evidenceModalPayload = null;
                    this.$refs.evidenceForceConfirmModal?.hide();
                    this.$emit("refresh");
                    this.$bvToast?.toast(response.data.message ?? "체크포인트가 검증되었습니다.", {
                        variant: "success",
                        title: "완료",
                    });
                } else {
                    this.saving = true;
                    const response = await carbonApi.workflow.createInventoryWorkflow({
                        ...payload,
                        forceConfirm: forceConfirmValue,
                    });
                    unwrapResponse(response);
                    this.evidenceModalError = null;
                    this.evidenceModalPayload = null;
                    this.$refs.evidenceForceConfirmModal?.hide();
                    this.$refs.workflowModal?.hide();
                    this.$emit("refresh");
                    this.$bvToast?.toast("워크플로우가 생성되었습니다.", {
                        variant: "success",
                        title: "생성 완료",
                    });
                }
            } catch (error) {
                this.$bvToast?.toast(
                    extractErrorMessage(
                        error,
                        isCheckpoint ? "강제 검증에 실패했습니다." : "강제 생성에 실패했습니다.",
                    ),
                    { variant: "danger", title: isCheckpoint ? "검증 실패" : "저장 실패" },
                );
            } finally {
                this.saving = false;
            }
        },
        onEvidenceModalHidden() {
            this.evidenceModalError = null;
            this.evidenceModalPayload = null;
        },
        async handleDelete() {
            if (!this.selectedStep?.workflowIdx) return;
            try {
                await carbonApi.workflow.deleteInventoryWorkflow(this.selectedStep.workflowIdx);
                this.$emit("refresh");
            } catch (error) {
                console.error(error);
            }
        },
    },
};
</script>

<style scoped>
/* ============================================================
   워크플로우 타임라인 — 프리미엄 탄소 배출 플랫폼
   진행 트랙: 극도로 은은 / 체크포인트: 강렬하게 강조
   ============================================================ */

/* ── 전체 컨테이너 ── */
.workflow-timeline {
    width: 100%;
    background: linear-gradient(160deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 12px;
    padding: 10px 8px;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow:
        0 1px 4px rgba(15, 23, 42, 0.05),
        0 4px 16px rgba(15, 23, 42, 0.03);
    margin-top: 8px;
    height: 100%;
}

/* ── 헤더 ── */
.timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 8px;
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.55);
}

.timeline-title {
    font-size: 10px;
    font-weight: 800;
    color: #334155;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    position: relative;
    padding-left: 12px;
}

.timeline-title::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 12px;
    background: linear-gradient(180deg, #14b8a6 0%, #0d9488 100%);
    border-radius: 2px;
}

/* ── 타임라인 레이아웃 ── */
.timeline-wrapper {
    width: 100%;
    height: 100%;
}

.timeline-container {
    position: relative;
    min-width: 100%;
    width: 100%;
    height: 100%;
    overflow: visible; /* 드롭다운 메뉴가 잘리지 않도록 */
}

/* ────────────────────────────────────────────────────────────
   진행 트랙 & 바 — 고급스럽되 마커보다 덜 눈에 띄게
   ──────────────────────────────────────────────────────────── */
.timeline-track {
    position: absolute;
    top: 79px;
    left: 60px;
    right: 60px;
    height: 4px;
    /* 미완료 구간: 은은한 대시 패턴 → "앞으로 남은 단계" 암시 */
    background: repeating-linear-gradient(
        90deg,
        rgba(203, 213, 225, 0.3) 0px,
        rgba(203, 213, 225, 0.3) 5px,
        transparent 5px,
        transparent 10px
    );
    border-radius: 3px;
    z-index: 1;
    overflow: hidden;
}

.workflow-timeline.compact .timeline-wrapper {
    min-height: 0;
}

.timeline-progress-bar {
    position: absolute;
    height: 100%;
    /* 완료 구간: 마커 색(틸→에메랄드)과 동일 계열 */
    background: linear-gradient(
        90deg,
        rgba(13, 148, 136, 0.72) 0%,
        rgba(20, 184, 166, 0.85) 55%,
        rgba(16, 185, 129, 0.78) 100%
    );
    border-radius: 3px;
    transition: width 0.95s cubic-bezier(0.34, 1, 0.64, 1);
    overflow: hidden;
}

/* 진행 바 위 주기적 shimmer 광택 */
.timeline-progress-bar::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.6) 50%, transparent 100%);
    transform: translateX(-100%);
    animation: progress-shimmer 5s ease-in-out infinite 2s;
}

@keyframes progress-shimmer {
    0% {
        transform: translateX(-100%);
    }
    40%,
    100% {
        transform: translateX(350%);
    }
}

/* ── 타임라인 리스트 ── */
.timeline-list {
    display: flex;
    justify-content: space-between;
    list-style: none;
    padding: 0;
    margin: 0;
    position: relative;
    z-index: 2;
    height: 100%;
    overflow: visible; /* 드롭다운 메뉴가 잘리지 않도록 */
}

.timeline-item {
    font-size: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: visible; /* 드롭다운 메뉴가 잘리지 않도록 */
    position: relative;
    isolation: isolate;
    padding: 10px 8px;
    border-radius: 10px;
    border: 1px solid transparent;
}

/* ── 선택 상태 ── */
.timeline-item.is-selected .marker-wrapper .timeline-marker {
    border-color: #059669;
    border-width: 2px;
    box-shadow:
        0 0 0 5px rgba(16, 185, 129, 0.14),
        0 6px 20px rgba(16, 185, 129, 0.2);
    transform: scale(1.1);
}

.timeline-item.is-selected .marker-wrapper .timeline-marker i {
    color: #059669;
}

.timeline-item.is-selected.status-completed .marker-wrapper .timeline-marker i {
    color: #fff;
}

/* ── 날짜 & 뱃지 행 ── */
.timeline-date {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    gap: 4px;
    margin-bottom: 8px;
    width: 100%;
}

.timeline-date-row-badges {
    width: 100%;
    height: 22px;
    min-height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.timeline-date-row-periods {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.timeline-date .period {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.period {
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    letter-spacing: 0.03em;
}

/* ────────────────────────────────────────────────────────────
   체크포인트 뱃지 — 강렬하게 눈에 띄도록 ★★★
   ──────────────────────────────────────────────────────────── */
.checkpoint-badge {
    width: 100%;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5px 10px;
    border-radius: 7px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    white-space: nowrap;
}

.checkpoint-badge-quarter {
    line-height: 1.3;
}

/* 검증완료 뱃지: 주황 그라데이션 + 강한 글로우 (미검증과 동일 계열) */
.checkpoint-badge-verified {
    background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
    color: #fff;
    box-shadow:
        0 2px 10px rgba(217, 119, 6, 0.5),
        0 0 0 2px rgba(217, 119, 6, 0.22);
}

/* 대기 뱃지: 앰버/골드 그라데이션 + 글로우 */
.checkpoint-badge-pending {
    background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
    color: #fff;
    box-shadow:
        0 2px 10px rgba(180, 83, 9, 0.42),
        0 0 0 2px rgba(217, 119, 6, 0.2);
}

/* ────────────────────────────────────────────────────────────
   체크포인트 아이템 카드 — 강하게 강조 ★★★
   ──────────────────────────────────────────────────────────── */
/* 검증완료 체크포인트 카드 (주황) */
.timeline-item.checkpoint-verified {
    background: linear-gradient(160deg, rgba(217, 119, 6, 0.08) 0%, rgba(180, 83, 9, 0.04) 100%);
    border: 1.5px solid rgba(217, 119, 6, 0.5);
    box-shadow:
        0 0 0 3px rgba(217, 119, 6, 0.09),
        0 6px 24px rgba(217, 119, 6, 0.14),
        inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

/* 대기 체크포인트 카드 */
.timeline-item.checkpoint-pending {
    background: linear-gradient(160deg, rgba(217, 119, 6, 0.07) 0%, rgba(180, 83, 9, 0.03) 100%);
    border: 1.5px solid rgba(217, 119, 6, 0.42);
    box-shadow:
        0 0 0 3px rgba(217, 119, 6, 0.08),
        0 6px 24px rgba(217, 119, 6, 0.11),
        inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

/* 체크포인트 대기 마커 */
.timeline-item.checkpoint-pending .timeline-marker {
    border: 2px solid rgba(217, 119, 6, 0.55);
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    box-shadow:
        0 0 0 4px rgba(217, 119, 6, 0.1),
        0 4px 14px rgba(217, 119, 6, 0.16);
}

.timeline-item.checkpoint-pending .timeline-marker i {
    color: #b45309;
}

/* ── 체크포인트 검증 버튼 ── */
.checkpoint-confirm-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: auto;
}

.btn-checkpoint-confirm {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 5px 10px;
    background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
    border: none;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(217, 119, 6, 0.32);
}

.btn-checkpoint-confirm:hover {
    background: linear-gradient(135deg, #92400e 0%, #b45309 100%);
    box-shadow: 0 4px 14px rgba(217, 119, 6, 0.42);
    transform: translateY(-1px);
    color: #fff;
}

.btn-checkpoint-confirm:disabled {
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
    color: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
}

.btn-checkpoint-confirm:disabled:hover {
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
    color: #94a3b8;
    transform: none;
    box-shadow: none;
}

/* ────────────────────────────────────────────────────────────
   마커 (노드)
   ──────────────────────────────────────────────────────────── */
.marker-wrapper {
    width: 42px;
    height: 42px;
    margin-bottom: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: 50%;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* 스텝 클릭 시 드롭다운 (인벤토리/활동자료) */
.step-action-dropdown {
    display: inline-flex;
}
.step-action-dropdown .step-action-toggle {
    padding: 0;
    border: none;
    background: none;
    text-decoration: none;
    box-shadow: none;
}
.step-action-dropdown .step-action-toggle:hover,
.step-action-dropdown .step-action-toggle:focus {
    background: none;
    border: none;
    box-shadow: none;
}
.step-action-menu {
    min-width: 140px;
    z-index: 1050; /* 드롭다운이 다른 요소 위에 표시되도록 */
}
.step-action-menu .dropdown-item-icon {
    width: 18px;
    margin-right: 8px;
    color: #64748b;
}

.marker-wrapper:hover {
    transform: scale(1.08);
}

/* 드롭다운 트리거: hover 시 scale 제거 → 마우스 이동 시 드롭다운이 흔들리지 않도록 */
.marker-wrapper-with-dropdown:hover {
    transform: none;
}

.marker-wrapper:hover .timeline-marker {
    border-color: #10b981;
    box-shadow:
        0 6px 20px rgba(0, 0, 0, 0.1),
        0 0 0 4px rgba(16, 185, 129, 0.15);
}

.marker-wrapper:hover .timeline-marker i {
    color: #10b981;
}

.status-completed .marker-wrapper:hover .timeline-marker i {
    color: #fff;
}

.status-in-progress .marker-wrapper:hover .timeline-marker i {
    color: #60a5fa;
}

.status-collection-done .marker-wrapper:hover .timeline-marker i,
.status-calculated .marker-wrapper:hover .timeline-marker i {
    color: #059669;
}

.timeline-marker {
    width: 42px;
    height: 42px;
    background: #fff;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1.5px solid #e2e8f0;
    flex-shrink: 0;
    box-shadow:
        0 2px 8px rgba(15, 23, 42, 0.07),
        0 1px 3px rgba(15, 23, 42, 0.04),
        inset 0 1px 0 rgba(255, 255, 255, 0.85);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.timeline-marker i {
    color: #94a3b8;
    font-size: 18px;
}

/* 상태별 마커 */
.status-completed .timeline-marker {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    border-color: transparent;
    box-shadow:
        0 4px 14px rgba(16, 185, 129, 0.38),
        0 0 0 3px rgba(16, 185, 129, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.status-completed .timeline-marker i {
    color: #fff;
    font-size: 16px;
}

/* 검증완료 체크포인트 마커: 더 크고 강렬한 글로우 ★ */
.timeline-item.checkpoint-verified.status-completed .timeline-marker {
    background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
    border-color: transparent;
    /* width: 48px;
    height: 48px; */
    box-shadow:
        0 0 0 5px rgba(217, 119, 6, 0.18),
        0 6px 22px rgba(217, 119, 6, 0.38),
        inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.timeline-item.checkpoint-verified.status-completed .marker-wrapper {
    /* width: 50px;
    height: 50px; */
}

/* 수집중: 파란 테두리 */
.status-in-progress .timeline-marker {
    border: 1.5px solid #93c5fd;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    box-shadow:
        0 2px 8px rgba(96, 165, 250, 0.16),
        0 0 0 3px rgba(96, 165, 250, 0.08);
}

.status-in-progress .timeline-marker i {
    color: #60a5fa;
}

/* 수집완료 대기: 연한 초록 */
.status-collection-done .timeline-marker {
    border: 1.5px solid #6ee7b7;
    background: linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%);
    box-shadow:
        0 2px 8px rgba(110, 231, 183, 0.2),
        0 0 0 3px rgba(110, 231, 183, 0.08);
}

.status-collection-done .timeline-marker i {
    color: #059669;
}

/* 산정완료: 초록 */
.status-calculated .timeline-marker {
    border: 1.5px solid #34d399;
    background: linear-gradient(135deg, #f0fdf4 0%, #d1fae5 100%);
    box-shadow:
        0 2px 8px rgba(52, 211, 153, 0.2),
        0 0 0 3px rgba(52, 211, 153, 0.08);
}

.status-calculated .timeline-marker i {
    color: #059669;
}

/* 대기: 그레이 */
.timeline-item.status-pending:not(.checkpoint-pending) .timeline-marker {
    border-color: #e8edf3;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.timeline-item.status-pending:not(.checkpoint-pending) .timeline-marker i {
    color: #c8d3de;
    font-size: 16px;
}

/* ── 하단 상태 요약 ── */
.status-summary {
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border-top: 1px solid rgba(226, 232, 240, 0.65);
    box-sizing: border-box;
    padding-top: 8px;
    margin-top: 8px;
    gap: 0;
}

.status-summary-top {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex-shrink: 0;
    margin: 2px 0;
}

.status-summary-spacer {
    flex: 1 1 auto;
    min-height: 12px;
}

/* ── 배출량 영역 ── */
.step-emissions {
    flex-shrink: 0;
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.55);
}

.step-emissions-total {
    display: flex;
    align-items: baseline;
    gap: 3px;
    justify-content: center;
    margin-bottom: 6px;
}

.step-emissions-value {
    font-size: 13px;
    font-weight: 800;
    color: #047857;
    letter-spacing: -0.03em;
    line-height: 1;
}

.step-emissions-scopes {
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 6px;
    row-gap: 3px;
    font-size: 10px;
    color: #64748b;
    padding-top: 5px;
    margin-top: 5px;
    border-top: 1px solid rgba(226, 232, 240, 0.5);
}

.step-emissions-scope-row {
    display: contents;
}

.step-emissions-scope-row .step-emissions-scope-label {
    text-align: left;
}

.step-emissions-scope-row .step-emissions-scope-value {
    text-align: right;
    font-weight: 700;
    color: #475569;
}

.step-emissions-scope-label {
    font-weight: 700;
    color: #94a3b8;
    font-size: 9px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}

/* ────────────────────────────────────────────────────────────
   상태 칩 (수집/산정) — 전체 배경 틴트 + 도트 인디케이터
   ──────────────────────────────────────────────────────────── */
.status-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px;
    border-radius: 20px;
    background: rgba(241, 245, 249, 0.75);
    border: 1px solid rgba(226, 232, 240, 0.5);
    transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: default;
    min-height: 22px;
}

/* 수집 상태별 배경 틴트 */
.chip-coll-collecting {
    background: rgba(239, 246, 255, 0.88);
    border-color: rgba(147, 197, 253, 0.38);
}
.chip-coll-completed {
    background: rgba(240, 253, 244, 0.88);
    border-color: rgba(110, 231, 183, 0.38);
}
/* 산정 상태별 배경 틴트 */
.chip-calc-calculated {
    background: rgba(240, 253, 244, 0.88);
    border-color: rgba(110, 231, 183, 0.38);
}
.chip-calc-verified {
    background: rgba(240, 253, 250, 0.88);
    border-color: rgba(45, 212, 191, 0.38);
}

/* 클릭 가능 상태 */
.status-chip.is-clickable {
    cursor: pointer;
}

.status-chip.is-clickable:hover {
    background: rgba(240, 253, 244, 0.96);
    border-color: rgba(16, 185, 129, 0.38);
    box-shadow: 0 1px 6px rgba(16, 185, 129, 0.12);
    transform: translateX(1px);
}

/* 팝오버 열림 상태 칩 */
.status-chip.is-open {
    background: rgba(240, 253, 244, 0.96);
    border-color: rgba(16, 185, 129, 0.38);
    box-shadow: 0 1px 6px rgba(16, 185, 129, 0.12);
    transform: translateX(0);
}

/* ── 칩 래퍼 (팝오버 기준점) ── */
.chip-wrap {
    position: relative;
}

/* ── 상태 전이 팝오버 ── */
.status-popup {
    position: absolute;
    bottom: calc(100% + 5px);
    left: 50%;
    transform: translateX(-50%);
    background: #ffffff;
    border: 1px solid rgba(203, 213, 225, 0.85);
    border-radius: 8px;
    box-shadow:
        0 4px 16px rgba(15, 23, 42, 0.1),
        0 1px 4px rgba(15, 23, 42, 0.06);
    padding: 3px;
    z-index: 1100;
    min-width: 108px;
    white-space: nowrap;
}

.popup-item {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 5px 8px;
    border-radius: 5px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    text-align: left;
    transition:
        background 0.12s ease,
        color 0.12s ease;
}

.popup-item:hover {
    background: rgba(240, 253, 244, 0.88);
    color: #059669;
}

.popup-item-backward {
    color: #64748b;
}

.popup-item-backward:hover {
    background: rgba(248, 250, 252, 0.9);
    color: #374151;
}

.popup-item-icon {
    font-size: 9px;
    flex-shrink: 0;
    opacity: 0.65;
    width: 10px;
    text-align: center;
}

.popup-item-label {
    line-height: 1;
}

/* 팝오버 열림 시 화살표 회전 */
.chip-arrow-open {
    opacity: 1 !important;
    transform: rotate(180deg) !important;
    color: #10b981 !important;
    animation: none !important;
}

/* ── 팝오버 Transition ── */
.popup-fade-enter-active,
.popup-fade-leave-active {
    transition:
        opacity 0.13s ease,
        transform 0.13s ease;
}

.popup-fade-enter,
.popup-fade-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(5px);
}

/* ── 상태 도트 인디케이터 ── */
.chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: #cbd5e1;
    transition:
        background 0.2s ease,
        box-shadow 0.2s ease;
}

.chip-coll-collecting .chip-dot {
    background: #60a5fa;
    animation: dot-breathe 2s ease-in-out infinite;
}
.chip-coll-completed .chip-dot {
    background: #10b981;
}
.chip-calc-calculated .chip-dot {
    background: #10b981;
}
.chip-calc-verified .chip-dot {
    background: #14b8a6;
}

/* 수집중: 도트 숨쉬기 애니메이션 */
@keyframes dot-breathe {
    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(96, 165, 250, 0.55);
    }
    50% {
        box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.12);
    }
}

/* hover 시 도트 → 초록 전환 */
.status-chip.is-clickable:hover .chip-dot {
    background: #10b981;
    animation: none;
    box-shadow: none;
}

.chip-label {
    font-size: 9px;
    font-weight: 800;
    color: #94a3b8;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    flex-shrink: 0;
    transition: color 0.18s ease;
}

.status-chip.is-clickable:hover .chip-label {
    color: #34d399;
}

.chip-value {
    font-size: 10px;
    font-weight: 600;
    color: #475569;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 상태별 value 색상 */
.chip-coll-collecting .chip-value {
    color: #3b82f6;
    font-weight: 700;
}
.chip-coll-completed .chip-value {
    color: #059669;
    font-weight: 700;
}
.chip-calc-calculated .chip-value {
    color: #059669;
    font-weight: 700;
}
.chip-calc-verified .chip-value {
    color: #0d9488;
    font-weight: 700;
}

.chip-arrow {
    font-size: 8px;
    color: #cbd5e1;
    flex-shrink: 0;
    opacity: 0;
    transform: translateX(-4px);
    transition:
        opacity 0.15s ease,
        transform 0.15s ease,
        color 0.15s ease;
}

.status-chip.is-clickable:hover .chip-arrow {
    opacity: 1;
    transform: translateX(0);
    color: #10b981;
}

/* ── Placeholder ── */
.timeline-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    color: #94a3b8;
    text-align: center;
    height: 100%;
}

.timeline-placeholder i {
    font-size: 111px;
    color: #e2e8f0;
    margin-bottom: 10px;
}

.timeline-placeholder .placeholder-title {
    font-size: 21px;
    font-weight: 700;
    color: #94a3b8;
    margin: 0 0 4px 0;
    letter-spacing: 0.02em;
}

.timeline-placeholder .placeholder-desc {
    font-size: 12px;
    color: #cbd5e1;
    margin: 0;
    line-height: 1.6;
}
</style>
